import React, { useEffect, useMemo, useState } from "react";
import { Labelbox } from "..";
import { Toaster } from "../../utils/common";
import { CLIENT_ACTION } from "../../redux/action";
import {
  buildClientBusinessDropdownOptions,
  getServiceCategoryLabel,
  getServiceClientBusinessId,
  getServiceNameOptionsForCategory,
  normalizeServiceStatus,
  SERVICE_CATEGORY_BUSINESS,
  SERVICE_CATEGORY_OPTIONS,
  SERVICE_DEFAULT_STATUS,
  SERVICE_FREQUENCY_OPTIONS,
  SERVICE_STATUS_OPTIONS,
  SERVICE_TAX_YEAR_OPTIONS,
} from "../../constants/clientServiceOptions";
import "./client-service-request-form.scss";

const emptyForm = () => ({
  start_date: new Date().toISOString().slice(0, 10),
  service_category: "",
  service_name: "",
  tax_year: "",
  service_frequency: "",
  client_business_id: "",
  status: SERVICE_DEFAULT_STATUS,
});

const CUSTOM_SERVICE_OPTION = "Custom";

function resolveEditedServiceName(serviceName, customServiceName, category) {
  const name = String(serviceName || "").trim();
  const custom = String(customServiceName || "").trim();
  if (!name) {
    return { service_name: "", custom_service_name: "" };
  }
  const options = getServiceNameOptionsForCategory(category);
  const isKnownOption = options.some(
    (option) => option.id === name && name !== CUSTOM_SERVICE_OPTION,
  );
  if (isKnownOption) {
    return { service_name: name, custom_service_name: "" };
  }
  return {
    service_name: CUSTOM_SERVICE_OPTION,
    custom_service_name: name === CUSTOM_SERVICE_OPTION ? custom || name : name,
  };
}

export default function ClientServiceRequestForm({
  defaultCategory = "individual",
  lockCategory = true,
  initialValues = null,
  submitting = false,
  onSubmit,
  onCancel,
  submitLabel = "Send Request",
  showCancel = true,
  showStatus = false,
  clientId = "",
  businesses: businessesProp,
}) {
  const normalizedDefault =
    String(defaultCategory || "individual").toLowerCase() === "business"
      ? "business"
      : "individual";

  const [form, setForm] = useState(() => {
    const resolved = resolveEditedServiceName(
      initialValues?.service_name,
      initialValues?.custom_service_name,
      normalizedDefault,
    );
    return {
      ...emptyForm(),
      service_category: normalizedDefault,
      client_business_id: getServiceClientBusinessId(initialValues),
      ...(initialValues || {}),
      service_name: resolved.service_name,
    };
  });
  const [customServiceName, setCustomServiceName] = useState(() =>
    resolveEditedServiceName(
      initialValues?.service_name,
      initialValues?.custom_service_name,
      normalizedDefault,
    ).custom_service_name,
  );
  const [loadedBusinesses, setLoadedBusinesses] = useState([]);

  const isBusinessCategory =
    String(
      lockCategory ? normalizedDefault : form.service_category || normalizedDefault,
    )
      .trim()
      .toLowerCase() === SERVICE_CATEGORY_BUSINESS;

  useEffect(() => {
    if (!isBusinessCategory) {
      setLoadedBusinesses([]);
      return undefined;
    }
    if (Array.isArray(businessesProp)) {
      setLoadedBusinesses(businessesProp);
      return undefined;
    }
    if (!clientId) {
      setLoadedBusinesses([]);
      return undefined;
    }
    let cancelled = false;
    CLIENT_ACTION.getClientBusinesses(clientId).then((list) => {
      if (!cancelled) setLoadedBusinesses(Array.isArray(list) ? list : []);
    });
    return () => {
      cancelled = true;
    };
  }, [isBusinessCategory, clientId, businessesProp]);

  const businessOptions = useMemo(
    () => buildClientBusinessDropdownOptions(loadedBusinesses),
    [loadedBusinesses],
  );

  // When used for editing an existing service, prefill form values.
  useEffect(() => {
    if (!initialValues) return;
    const resolved = resolveEditedServiceName(
      initialValues.service_name,
      initialValues.custom_service_name,
      normalizedDefault,
    );
    setForm((prev) => ({
      ...prev,
      service_category: normalizedDefault,
      start_date: initialValues.start_date ?? prev.start_date,
      service_name: resolved.service_name,
      tax_year: initialValues.tax_year ?? prev.tax_year,
      service_frequency:
        initialValues.service_frequency ?? prev.service_frequency,
      client_business_id:
        getServiceClientBusinessId(initialValues) || prev.client_business_id,
      status: normalizeServiceStatus(initialValues.status ?? prev.status),
    }));
    setCustomServiceName(resolved.custom_service_name);
  }, [initialValues, normalizedDefault]);

  useEffect(() => {
    const resolved = resolveEditedServiceName(
      initialValues?.service_name,
      initialValues?.custom_service_name,
      normalizedDefault,
    );
    setForm((prev) => ({
      ...prev,
      service_category: normalizedDefault,
      service_name: resolved.service_name,
      client_business_id: getServiceClientBusinessId(initialValues),
    }));
    setCustomServiceName(resolved.custom_service_name);
  }, [normalizedDefault, initialValues]);

  const serviceNameOptions = useMemo(
    () => getServiceNameOptionsForCategory(form.service_category),
    [form.service_category],
  );

  const updateField = (name, value) => {
    setForm((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "service_category") {
        next.service_name = "";
        if (String(value || "").toLowerCase() !== SERVICE_CATEGORY_BUSINESS) {
          next.client_business_id = "";
        }
      }
      return next;
    });
    if (name === "service_category" || name === "service_name") {
      if (name === "service_category" || value !== CUSTOM_SERVICE_OPTION) {
        setCustomServiceName("");
      }
    }
  };

  useEffect(() => {
    if (!isBusinessCategory) return;
    setForm((prev) => {
      if (prev.client_business_id) return prev;
      if (businessOptions.length === 1) {
        return { ...prev, client_business_id: businessOptions[0].id };
      }
      return prev;
    });
  }, [isBusinessCategory, businessOptions]);

  const handleSubmit = (event) => {
    event.preventDefault();
    const resolvedServiceName =
      form.service_name === CUSTOM_SERVICE_OPTION
        ? String(customServiceName || "").trim()
        : String(form.service_name || "").trim();

    if (!resolvedServiceName) {
      Toaster.warning(
        form.service_name === CUSTOM_SERVICE_OPTION
          ? "Please describe the service you need"
          : "Please select a service name",
      );
      return;
    }

    const category = String(form.service_category || normalizedDefault)
      .trim()
      .toLowerCase();
    if (category === SERVICE_CATEGORY_BUSINESS && !form.client_business_id) {
      Toaster.warning("Please select a business");
      return;
    }

    onSubmit?.({
      ...form,
      service_name: resolvedServiceName,
      custom_service_name:
        form.service_name === CUSTOM_SERVICE_OPTION ? resolvedServiceName : "",
      client_business_id:
        category === SERVICE_CATEGORY_BUSINESS
          ? String(form.client_business_id || "").trim()
          : "",
      status: normalizeServiceStatus(form.status),
    });
  };

  const categoryLabel = getServiceCategoryLabel(
    lockCategory ? normalizedDefault : form.service_category,
  );
  const isCustomService = form.service_name === CUSTOM_SERVICE_OPTION;

  return (
    <form className="client-service-request-form" onSubmit={handleSubmit}>
      {/* <p className="client-service-request-form__intro">
        You have selected <strong>{categoryLabel} Service</strong>. Please
        select the {categoryLabel} Service Type &amp; click Send Request.
      </p> */}

      <div className="client-service-request-form__field">
        <Labelbox
          type="datepicker"
          labelname="Start Date"
          value={form.start_date}
          changeData={(value) => updateField("start_date", value)}
        />
      </div>

      <div className="client-service-request-form__field">
        {lockCategory ? (
          <div className="client-service-request-form__category-readonly">
            <span className="client-service-request-form__category-label">
              Service Category
            </span>
            <div
              className="client-service-request-form__category-value"
              aria-readonly="true"
            >
              {categoryLabel}
            </div>
          </div>
        ) : (
          <Labelbox
            type="select"
            labelname="Service Category"
            value={form.service_category}
            changeData={(value) => updateField("service_category", value)}
            dropdown={SERVICE_CATEGORY_OPTIONS}
          />
        )}
      </div>

      <div className="client-service-request-form__field">
        <Labelbox
          type="select"
          labelname="Service Name"
          value={form.service_name}
          changeData={(value) => updateField("service_name", value)}
          dropdown={serviceNameOptions}
        />
      </div>

      {isBusinessCategory ? (
        <div className="client-service-request-form__field">
          <Labelbox
            type="select"
            labelname="Business Name"
            value={form.client_business_id}
            changeData={(value) => updateField("client_business_id", value)}
            dropdown={businessOptions}
          />
        </div>
      ) : null}

      {isCustomService ? (
        <div className="client-service-request-form__field">
          <Labelbox
            type="text"
            labelname="Describe your service"
            value={customServiceName}
            changeData={setCustomServiceName}
            placeholder="Enter the service you need"
          />
        </div>
      ) : null}

      <div className="client-service-request-form__field">
        <Labelbox
          type="select"
          labelname="Tax Year"
          value={form.tax_year}
          changeData={(value) => updateField("tax_year", value)}
          dropdown={SERVICE_TAX_YEAR_OPTIONS}
        />
      </div>

      <div className="client-service-request-form__field">
        <Labelbox
          type="select"
          labelname="Service Frequency"
          value={form.service_frequency}
          changeData={(value) => updateField("service_frequency", value)}
          dropdown={SERVICE_FREQUENCY_OPTIONS}
        />
      </div>

      {showStatus ? (
        <div className="client-service-request-form__field">
          <Labelbox
            type="select"
            labelname="Status"
            value={form.status || SERVICE_DEFAULT_STATUS}
            changeData={(value) => updateField("status", value)}
            dropdown={SERVICE_STATUS_OPTIONS}
          />
        </div>
      ) : null}

      <div className="client-service-request-form__actions">
        {showCancel ? (
          <button
            type="button"
            className="client-service-request-form__cancel-btn"
            onClick={onCancel}
            disabled={submitting}
          >
            Cancel
          </button>
        ) : null}
        <button
          type="submit"
          className="client-service-request-form__submit-btn"
          disabled={submitting}
        >
          {submitting ? "Sending..." : submitLabel}
        </button>
      </div>
    </form>
  );
}
